import { useEffect, useContext, useCallback } from 'react';
import { useTracking } from 'react-tracking';
import Media from 'react-media';

import {
  Text,
  Grid,
  Heading,
  Button,
  CrossSolid,
  PlusSolid,
  useTheme,
} from '@simplywallst/ui-core';
import { useUserAgent } from '@/context/UserAgent';

import Pill from '@/components/Pill';
import {
  Note,
  useCompanyNotes,
  EmptyState,
  type NoteFragmentType,
  CompanyNoteContext,
} from '@/components/Note';
import { SubSectionHeader } from '../../Overview/components/SubSectionHeader';
import { SideMenuContext } from '../../Overview/components/SideMenu/context';
import { Section } from '../../Overview/components/Section';

import {
  StyledHeaderGrid,
  NotesSidebarList,
  SidebarHeader,
  NotesContainer,
  AddButton,
} from './styled';

interface Props {
  companyId?: string;
  uniqueSymbol?: string;
}

const getColumns = (total: number, max: number) => {
  if (total <= 0) {
    return 1;
  }
  if (total >= max) {
    return max;
  }

  return total;
};

export const MyNotes = ({ companyId, uniqueSymbol }: Props) => {
  const { openNoteModal, modalState } = useContext(CompanyNoteContext);
  const { isMobile, isDesktop, isTablet } = useUserAgent();
  const theme = useTheme();
  const { data } = useCompanyNotes(companyId);
  const { createSideMenu, currentId, setShowMenu, showMenu } =
    useContext(SideMenuContext);
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'MyNotes',
    companyId,
    uniqueSymbol,
  });

  const noData = typeof data === 'undefined' || data.length === 0;
  const canCreateNote = !!companyId && !!uniqueSymbol;
  useEffect(() => {
    if (canCreateNote && modalState === 'adding') {
      openNoteModal({ companyId, uniqueSymbol });
    }
  }, [companyId, uniqueSymbol, modalState]);

  const handleAddNote = useCallback(() => {
    if (!canCreateNote) {
      return;
    }
    openNoteModal({ companyId, uniqueSymbol });
  }, [companyId, uniqueSymbol]);

  const handleEditNote = useCallback(
    (note: NoteFragmentType) => {
      if (!canCreateNote) {
        return;
      }
      openNoteModal({ note, companyId, uniqueSymbol });
    },
    [companyId, uniqueSymbol]
  );

  useEffect(() => {
    if (showMenu && currentId === 'my-notes' && noData) {
      return setShowMenu('closed');
    }

    if (typeof createSideMenu === 'function' && !noData) {
      createSideMenu(
        <NotesSidebarList data-cy-id="notes-sidemenu">
          <SidebarHeader
            templateColumns=" 1fr auto auto"
            gap="8px"
            justifyContent="start"
            alignItems="center"
          >
            <Heading as="h3">Notes</Heading>
            <AddButton
              data-cy-id="notes-sidemenu-add"
              styleType="gentleReverse"
              onClick={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'add-note',
                  subject: 'my-notes-side-menu',
                });
                handleAddNote();
              }}
            >
              <PlusSolid />
              &nbsp;Add
            </AddButton>
            <button
              data-cy-id="notes-sidemenu-close"
              onClick={() => setShowMenu('closed')}
            >
              <CrossSolid height={20} width={20} fill="white" opacity={0.5} />
            </button>
          </SidebarHeader>
          <Grid gap="8px">
            {data.map((n, i) => (
              <Note
                data-cy-id={`note-side-menu-${i}`}
                key={`note-side-menu-${n.id}`}
                note={n}
                editNote={handleEditNote}
              />
            ))}
          </Grid>
        </NotesSidebarList>,
        'my-notes'
      );
    }
  }, [data, uniqueSymbol, handleEditNote, handleAddNote]);

  return (
    <Track>
      <Section data-cy-id="my-notes">
        <StyledHeaderGrid
          as="header"
          templateColumns="auto auto 1fr"
          gap="8px"
          justifyContent="start"
          alignItems="center"
        >
          <SubSectionHeader>My Notes</SubSectionHeader>
          {data && (
            <Text as="span" typography="tiny">
              <Pill>{data.length}</Pill>
            </Text>
          )}
          <Grid justifyContent="end">
            {!noData && (
              <AddButton
                data-cy-id="my-notes-add"
                styleType="gentleReverse"
                onClick={() => {
                  trackEvent({
                    action: 'click',
                    modifier: 'add-note',
                    subject: 'my-notes-section-header',
                  });
                  handleAddNote();
                }}
              >
                <PlusSolid />
                &nbsp;Add
              </AddButton>
            )}
          </Grid>
        </StyledHeaderGrid>
        <NotesContainer
          templateColumnsMd={`repeat(${getColumns(data?.length || 0, 3)}, 1fr)`}
          templateColumnsSm={`repeat(${getColumns(data?.length || 0, 2)}, 1fr)`}
          templateColumns="1fr"
          gap="8px"
        >
          <Media
            queries={{
              mobile: { maxWidth: theme.xsMaxRaw },
              tablet: { minWidth: theme.smMinRaw, maxWidth: theme.smMaxRaw },
              desktop: { minWidth: theme.lgMinRaw },
            }}
            defaultMatches={{
              mobile: isMobile,
              tablet: isTablet,
              desktop: isDesktop,
            }}
          >
            {(matches) => {
              const item = matches.mobile ? 1 : matches.tablet ? 2 : 3;
              return noData ? (
                <EmptyState
                  canCreateNote={canCreateNote}
                  handleAddNote={() => {
                    trackEvent({
                      action: 'click',
                      modifier: 'add-note',
                      subject: 'note-empty-state',
                    });
                    handleAddNote();
                  }}
                />
              ) : (
                data
                  .slice(0, item)
                  .map((n, i) => (
                    <Note
                      data-cy-id={`note-${i}`}
                      key={`note-${n.id}`}
                      note={n}
                      editNote={handleEditNote}
                    />
                  ))
              );
            }}
          </Media>
        </NotesContainer>
        {!noData && (
          <Button
            data-cy-id="my-notes-see-more"
            disabled={showMenu}
            display="block"
            onClick={() => {
              setShowMenu('my-notes');
              trackEvent({
                action: 'click',
                modifier: 'see-more',
              });
            }}
            styleType="loud"
          >
            See more
          </Button>
        )}
      </Section>
    </Track>
  );
};
